import React, { useContext } from "react";
import { Box, Modal, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { CompanyStore } from "./types/storeType";
import AppButton from "../common/AppButton";
import { useDeleteStore } from "../../hooks/api/useStore";
import { localizedPath, KivuI18nContext } from "@kivunova/kivufrontendcommon";
import { useNavigate } from "react-router-dom";
import {
  PORTAL_PREFIX,
  PAGE_PORTAL_COMPANY
} from "@kivunova/kivufrontendcommon";

import { captureAppException } from "../../Utils/appLogger";

interface DeleteEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  store: CompanyStore;
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: 2,
  p: 4,
  maxWidth: 400,
  minWidth: 250,
  boxShadow: 24,
  textAlign: "center"
};

const DeleteStoreModal: React.FC<DeleteEmployeeModalProps> = ({
  open,
  onClose,
  store
}) => {
  const deleteStore = useDeleteStore();
  const navigate = useNavigate();

  const { language, defaultLang } = useContext(KivuI18nContext);

  const handleDelete = async () => {
    try {
      await deleteStore.mutateAsync(store.id);
      navigate(
        localizedPath(
          `${PORTAL_PREFIX}/${PAGE_PORTAL_COMPANY}`,
          language,
          defaultLang
        )
      );
      onClose();
    } catch (err) {
      captureAppException("DeleteStore: Failed to delete store", err);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle} color="black">
        <WarningAmberRoundedIcon color="warning" sx={{ fontSize: 48, mb: 2 }} />

        <Typography variant="h6" mb={1} fontSize={18} fontWeight={600}>
          Delete Store
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={3}>
          Are you sure you want to delete{" "}
          <strong>
            {store?.name} - {store?.id}
          </strong>
          ? This action cannot be undone.
        </Typography>

        <Box display="flex" justifyContent="center" gap={2}>
          <AppButton variant="secondary" onClick={onClose}>
            No
          </AppButton>
          <AppButton
            variant="danger"
            onClick={handleDelete}
            disabled={deleteStore.isPending}
          >
            {deleteStore.isPending ? "Deleting..." : "Yes, Delete"}
          </AppButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default DeleteStoreModal;
